import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { orderBy } from 'firebase/firestore';
import Breadcrumb from '../../../../components/bootstrap/Breadcrumb';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardBody, CardHeader, CardLabel, CardTitle } from '../../../../components/bootstrap/Card';
import showNotification from '../../../../components/extras/showNotification';
import notificationBody from '../../../../helpers/utils/functions/notificationBody';
import TableCustom from '../../../../components/MyCustom/TableCustom';
import SelectEstablishment from '../../../../components/MyCustom/SelectEstablishment';
import { DefaultContext } from '../../../../contexts/default';
import FiscalGroupDB from '../../../../database/wrappers/fiscalGroup';
import useDarkMode from '../../../../hooks/useDarkMode';
import SubHeader, { SubHeaderLeft, SubHeaderRight } from '../../../../layout/SubHeader/SubHeader';
import Page from '../../../../layout/Page/Page';
import PageWrapper from '../../../../layout/PageWrapper/PageWrapper';
import { demoPages } from '../../../../menu';
import { convertTrybuIntToPTBR } from '../../../../types/fiscal';
import { ACCESS_LEVEL } from '../../../../types/roles';
import ModalRegisterFiscalGroups from '../../../../components/modals/FiscalGroups/ModalRegister';

const FiscalGroups = () => {
  const { accessLevel, establishment, onShowQuestion } = useContext(DefaultContext);
  const { darkModeStatus } = useDarkMode();

  const [openModalRegister, setOpenModalRegister] = useState(false);
  const [fiscalGroups, setFiscalGroups] = useState([]);
  const [fiscalGroupSelected, setFiscalGroupSelected] = useState(null);

  useEffect(() => {
    if (!establishment) return;
    return new FiscalGroupDB(establishment.id).on(setFiscalGroups, orderBy('name', 'asc'));
  }, [establishment]);

  const handleOpenModalRegister = useCallback(() => {
    setFiscalGroupSelected(null);
    setOpenModalRegister(true);
  }, []);

  const handleCloseModalRegister = useCallback(() => {
    setOpenModalRegister(false);
  }, []);

  const handleOpenModalEdit = useCallback((item) => {
    setFiscalGroupSelected(item);
    setOpenModalRegister(true);
  }, []);

  const handleOpenModalDelete = useCallback((row) => {
    onShowQuestion({
      title: 'Exclusão de Grupo Fiscal',
      message: 'Você está excluindo um grupo fiscal. Esta ação é irreversível. Deseja continuar?',
      onConfirm: () => {
        new FiscalGroupDB(establishment.id)
          .delete(row.id)
          .then(() => showNotification('', notificationBody('Registro deletado com sucesso'), 'success'))
          .catch(() => showNotification('', notificationBody('Falhou ao deletar o registro'), 'danger'));
      }
    })
  }, [establishment]);

  const rowsRender = useMemo(() => fiscalGroups.map(item => ({
    ...item,
    fiscal_cst: item.fiscal_data?.cst || '',
    fiscal_csosn: item.fiscal_data?.csosn || '',
    fiscal_cest: item.fiscal_data?.cest || '',
    fiscal_cfop: item.fiscal_data?.cfop || '',
    fiscal_aliq_icms: item.fiscal_data?.aliq_icms || '',
    fiscal_cBenef: item.fiscal_data?.cBenef || '',
    fiscal_tributary_situation: item.fiscal_data?.tributary_situation !== undefined
      ? convertTrybuIntToPTBR(Number(item.fiscal_data?.tributary_situation))
      : '',
  })), [fiscalGroups]);

  const columns = useMemo(() => ([
    {
      label: 'Nome',
      field: 'name',
    },
    {
      label: 'CST',
      field: 'fiscal_cst',
    },
    {
      label: 'CSOSN',
      field: 'fiscal_csosn',
    },
    {
      label: 'CEST',
      field: 'fiscal_cest',
    },
    {
      label: 'CFOP',
      field: 'fiscal_cfop',
    },
    {
      label: 'ICMS',
      field: 'fiscal_aliq_icms',
    },
    {
      label: 'Cód. Benefício Fiscal',
      field: 'fiscal_cBenef',
    },
    {
      label: 'Sit. Tributária',
      field: 'fiscal_tributary_situation',
    },
    {
      label: 'Ações',
      field: 'actions',
      format: row => (
        <div className='w-50 d-flex justify-content-between'>
          <Button
            color='light'
            icon='edit'
            shadow='sm'
            hoverShadow='sm'
            size='sm'
            onClick={() => handleOpenModalEdit(row)}
          />
          <Button
            color='danger'
            icon='Trash'
            shadow='sm'
            hoverShadow='sm'
            size='sm'
            onClick={() => handleOpenModalDelete(row)}
          />
        </div>
      ),
      noExport: true
    }
  ]), [handleOpenModalEdit, handleOpenModalDelete]);

  return (
    <>
      <PageWrapper title={demoPages.pdvMenu.subMenu.fiscalGroups.text}>
        <SubHeader>
          <SubHeaderLeft>
            <Breadcrumb
              list={[
                { title: 'PDV', to: '/pdv/fiscal-groups' },
                {
                  title: 'Grupos fiscais',
                  to: '/pdv/fiscal-groups',
                },
              ]}
            />
          </SubHeaderLeft>
          <SubHeaderRight>
            {accessLevel >= ACCESS_LEVEL.MANAGER &&
              <Button
                style={{ color: 'black' }}
                color='warning'
                icon='plus'
                shadow='sm'
                hoverShadow='sm'
                onClick={handleOpenModalRegister}>
                Cadastrar
              </Button>}
          </SubHeaderRight>
        </SubHeader>

        <Page container='fluid'>
          <Card>
            <CardHeader>
              <CardLabel icon='ReceiptLong' iconColor={darkModeStatus ? 'light' : 'dark'}>
                <CardTitle>Grupos fiscais</CardTitle>
              </CardLabel>
            </CardHeader>

            <CardBody className='table-responsive'>
              <SelectEstablishment />
              <TableCustom
                titlePdf='Grupos fiscais'
                fontSize={8}
                rows={rowsRender}
                columns={columns}
                fileName='Grupos-Fiscais-PDV'
              />
            </CardBody>
          </Card>
        </Page>
      </PageWrapper>

      <ModalRegisterFiscalGroups
        open={openModalRegister}
        setIsOpen={setOpenModalRegister}
        setIsClose={handleCloseModalRegister}
        fiscalGroupSelected={fiscalGroupSelected}
      />
    </>
  );
};

export default FiscalGroups;
